<template>
  <div v-if="kbecsWithoutRequiredRole.length > 0" class="gray-lighten-2 p-2 font-medium">
    <span>Bedrijfspersonen die geen {{ allowedRolesString }} zijn</span>
  </div>

  <CustomComboboxOption
    v-for="wrappedNonServiceProvider in kbecsWithoutRequiredRole"
    :key="wrappedNonServiceProvider.item.id"
    :wrapper="wrappedNonServiceProvider"
    :text="wrappedNonServiceProvider.item.getDisplayText()"
    :disabled="disabled"
    @mousedown="$emit('optionMousedown', $event)"
    @mouseup="$emit('optionMouseup', $event)"
  >
    <template #after-text>
      <PBadge class="gray-lighten-1! ml-2">Rol toevoegen</PBadge>
    </template>
  </CustomComboboxOption>

  <div v-if="personMatches.length > 0" class="gray-lighten-2 p-2 font-medium">
    <span>Bedrijf toevoegen aan persoon</span>
  </div>
  <CustomComboboxOption
    v-for="wrappedPerson in personMatches"
    :key="wrappedPerson.item.id"
    :wrapper="wrappedPerson"
    :text="wrappedPerson.item.fullName"
    :disabled="disabled"
    @mousedown="$emit('optionMousedown', $event)"
    @mouseup="$emit('optionMouseup', $event)"
  >
    <template #after-text>
      <PBadge class="gray-lighten-1! ml-2">Bedrijf toevoegen</PBadge>
    </template>
  </CustomComboboxOption>
</template>

<script setup lang="ts">
import PBadge from "@/components/elements/PBadge.vue"
import { computed } from "vue"
import { storeToRefs } from "pinia"
import { CustomComboboxOptionWrapper } from "@/components/inputs/input_combobox/CustomComboboxOptionWrapper"
import type { KnownBusinessEntityContact } from "@/models/known-business-entity-contact.model.ts"
import { useKnownBusinessEntitiesContactsStore } from "@/store/known-business-entities-contacts.store.ts"
import { RoleTypes } from "@/enums/role-types"
import { fuzzySearchCollection } from "@/helper/collection-helper"
import { useModelFormStore } from "@/store/model-forms.store"
import { usePersonStore } from "@/store/persons.store"
import { getSubclassForRoleType } from "@/helper/role-helper"
import CustomComboboxOption from "@/components/inputs/input_combobox/CustomComboboxOption.vue"
import type { Person } from "@/models/person.model"
import type { FuseOptionKey } from "fuse.js"

defineEmits(["optionMousedown", "optionMouseup"])

const props = withDefaults(
  defineProps<{
    comboboxText: string
    saveHandle: (kbec: KnownBusinessEntityContact) => void
    closeHandle: () => void
    isCreatingNewItemHandle: (bool: boolean) => void
    allowedRoles?: RoleTypes[] | null
    disabled?: boolean
  }>(),
  {
    allowedRoles: null
  }
)

const knownBusinessEntitiesContactsStore = useKnownBusinessEntitiesContactsStore()
const { activeKnownBusinessEntitiesContacts } = storeToRefs(knownBusinessEntitiesContactsStore)

const modelFormStore = useModelFormStore()

const LIST_LIMIT = 10

const allowedRolesString = computed(() => {
  if (!props.allowedRoles) {
    return null
  }
  return props.allowedRoles
    .map((role) => getSubclassForRoleType(role).verboseName.toLowerCase())
    .join(" of ")
})

/**
 * KnownBusinessEntitiesContacts that are not service providers. This allows the user to add the service provider role.
 */
const kbecsWithoutRequiredRole = computed<
  CustomComboboxOptionWrapper<KnownBusinessEntityContact>[]
>(() => {
  if (!props.allowedRoles) {
    return []
  }

  const _kbecsWithoutRequiredRole = activeKnownBusinessEntitiesContacts.value.filter(
    (kbec) => kbec.knownBusinessEntity && props.allowedRoles!.every((role) => !kbec.hasRole(role))
  )
  const fuseOptionKeys: FuseOptionKey<KnownBusinessEntityContact>[] = [
    { name: "person.firstName" },
    { name: "person.lastName" },
    { name: "knownBusinessEntity.name" }
  ]
  return fuzzySearchCollection(_kbecsWithoutRequiredRole, fuseOptionKeys, props.comboboxText)
    .map((kbec) => {
      return new CustomComboboxOptionWrapper(kbec, () => {
        props.isCreatingNewItemHandle(true)
        modelFormStore.openForm({
          model: kbec.person,
          saveHandle: props.saveHandle,
          closeHandle: props.closeHandle,
          creationParameters: {
            focusRoleButton: kbec
          }
        })
      })
    })
    .slice(0, LIST_LIMIT)
})

const personStore = usePersonStore()
const { persons } = storeToRefs(personStore)
/**
 * Persons that do not have a KnownBusinessEntity. This allows the user to create the KnownBusinessEntityContact.
 */
const personMatches = computed(() => {
  return fuzzySearchCollection(
    persons.value,
    [{ name: "firstName" }, { name: "lastName" }],
    props.comboboxText
  )
    .map((person) => {
      return new CustomComboboxOptionWrapper(person, () => {
        props.isCreatingNewItemHandle(true)
        modelFormStore.openForm<Person>({
          model: person,
          saveHandle: props.saveHandle,
          closeHandle: props.closeHandle,
          creationParameters: {
            focusNewKnownBusinessEntityButton: true
          }
        })
      })
    })
    .slice(0, LIST_LIMIT)
})
</script>

<style scoped></style>
